<script setup lang="ts">
/**
 * Knowledge section in the matter-context modal's left pane. Inline
 * editor — no separate dialog. Idle state shows the placeholder (or the
 * saved text) as a click target; clicking expands an in-place textarea
 * with Save / Cancel below it. Cancel routes through a discard-changes
 * dialog if the draft is dirty.
 */
import { ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMatterContextModalStore } from '../../stores/matterContextModalStore';
import DiscardChangesDialog from './DiscardChangesDialog.vue';

const { t } = useI18n();
const store = useMatterContextModalStore();

const textareaRef = ref<HTMLTextAreaElement | null>(null);
const discardDialogVisible = ref(false);

watch(
    () => store.knowledgeEditorOpen,
    async (open) => {
        if (!open) return;
        await nextTick();
        textareaRef.value?.focus();
    },
);

const onIdleClick = () => {
    if (!store.inputsEditable) return;
    store.openKnowledgeEditor();
};

const onSave = () => {
    if (!store.knowledgeEditorDraft.trim()) return;
    store.saveKnowledgeEditor();
};

const onCancel = () => {
    if (store.knowledgeEditorDirty) {
        discardDialogVisible.value = true;
        return;
    }
    store.discardKnowledgeEditor();
};

const onDiscardConfirm = () => {
    discardDialogVisible.value = false;
    store.discardKnowledgeEditor();
};

const onDiscardCancel = () => {
    discardDialogVisible.value = false;
};
</script>

<template>
    <section class="mc-section mc-knowledge-section">
        <header class="mc-section-header">
            <h3 class="mc-section-title">{{ t('matterContext.knowledge.title') }}</h3>
        </header>

        <template v-if="store.knowledgeEditorOpen">
            <textarea
                ref="textareaRef"
                v-model="store.knowledgeEditorDraft"
                class="mc-knowledge-editor-textarea"
                :placeholder="t('matterContext.knowledge.placeholder')"
            ></textarea>
            <div class="mc-knowledge-editor-actions">
                <button
                    type="button"
                    class="mc-knowledge-save"
                    :disabled="!store.knowledgeEditorDraft.trim()"
                    @click="onSave"
                >
                    {{ t('common.save') }}
                </button>
                <button type="button" class="mc-knowledge-cancel" @click="onCancel">
                    {{ t('common.cancel') }}
                </button>
            </div>
        </template>

        <template v-else>
            <p
                v-if="!store.matterKnowledge.trim()"
                class="mc-knowledge-placeholder"
                :class="{ 'mc-knowledge-placeholder-disabled': !store.inputsEditable }"
                @click="onIdleClick"
            >
                {{ t('matterContext.knowledge.placeholder') }}
            </p>
            <p
                v-else
                class="mc-knowledge-text"
                :class="{ 'mc-knowledge-text-clickable': store.inputsEditable }"
                @click="onIdleClick"
            >
                {{ store.matterKnowledge }}
            </p>
        </template>

        <DiscardChangesDialog
            :visible="discardDialogVisible"
            @discard="onDiscardConfirm"
            @cancel="onDiscardCancel"
        />
    </section>
</template>
